import React, { useState, useEffect, useMemo } from "react";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./MilestoneHistory.css";

dayjs.extend(utc);
dayjs.extend(timezone);

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

const INITIAL_DISPLAY_COUNT = 5;

const getStatusType = (item) => {
    if (!item.rollbackStatus || item.rollbackStatus === "") {
        return "completed";
    }
    const rb = item.rollbackStatus.toLowerCase();
    if (rb === "reopened") return "reopened";
    if (rb.includes("return") || rb.includes("moved back")) return "moved-back";
    if (rb.includes("in progress") || rb.includes("inprogress")) return "in-progress";
    return "moved-back";
};

const getStatusLabel = (item) => {
    if (!item.rollbackStatus || item.rollbackStatus === "") {
        return "Completed";
    }
    return item.rollbackStatus;
};

const getAvatarColor = (name) => {
    const colors = ["#2563eb", "#7c3aed", "#0891b2", "#059669", "#d97706", "#dc2626"];
    let hash = 0;
    for (let i = 0; i < (name || "").length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.split(/[\s._-]+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return dayjs(dateStr).format("MM/DD/YYYY hh:mm A");
};

// SVG Icons
const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

const ArrowLeftIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <line x1="19" y1="12" x2="5" y2="12" />
        <polyline points="12 19 5 12 12 5" />
    </svg>
);

const InProgressIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3" fill="currentColor" />
    </svg>
);

const ReopenedIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="23 4 23 10 17 10" />
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
    </svg>
);

const ChevronDownIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);

const StatusIcon = ({ type }) => {
    switch (type) {
        case "completed": return <CheckIcon />;
        case "in-progress": return <InProgressIcon />;
        case "moved-back": return <ArrowLeftIcon />;
        case "reopened": return <ReopenedIcon />;
        default: return <CheckIcon />;
    }
};

const MilestoneHistoryPage = ({ loanId }) => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showAll, setShowAll] = useState(false);
    const [sortOrder, setSortOrder] = useState("desc"); // desc = newest first

    const fetchData = async () => {
        if (!loanId || isLoading) return;

        setIsLoading(true);
        setError(null);

        const payload = {
            filters: {
                loanid: loanId
            }
        };

        try {
            const response = await fetch(`${API_DOMAIN}/api/audit/milestonehistory/filter`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const result = await response.json();
            const items = result.data || [];
            setData(items);
        } catch (err) {
            console.error("Error fetching milestone history:", err);
            setError(err?.message || "Failed to fetch milestone history");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (loanId) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loanId]);

    const sortedData = useMemo(() => {
        const sorted = [...data].sort((a, b) => {
            const dateA = new Date(a.updatedDateTime || 0);
            const dateB = new Date(b.updatedDateTime || 0);
            return sortOrder === "desc" ? dateB - dateA : dateA - dateB;
        });
        return sorted;
    }, [data, sortOrder]);

    const displayedData = useMemo(() => {
        if (showAll) return sortedData;
        return sortedData.slice(0, INITIAL_DISPLAY_COUNT);
    }, [sortedData, showAll]);

    const toggleSortOrder = () => {
        setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    };

    if (isLoading) {
        return (
            <div className="milestone-history__loading">
                <Spin indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />} />
            </div>
        );
    }

    return (
        <div className="milestone-history">
            {/* Table Section */}
            <div className="milestone-history__table-section">
                <div className="milestone-history__header">
                    <h2 className="milestone-history__title">Milestone History</h2>
                    <p className="milestone-history__subtitle">
                        Complete history of milestone movements including forward progress, returns, and reopens.
                    </p>
                </div>

                {error && (
                    <div className="milestone-history__error">
                        <strong>Error:</strong> {error}
                    </div>
                )}

                {!error && data.length === 0 ? (
                    <div className="milestone-history__empty">
                        <div className="milestone-history__empty-icon">📋</div>
                        <p className="milestone-history__empty-text">No milestone history found for this loan.</p>
                    </div>
                ) : (
                    <>
                        <table className="milestone-history__table">
                            <thead>
                                <tr>
                                    <th>Status</th>
                                    <th>Completed Milestone</th>
                                    <th>Next Milestone</th>
                                    <th>Completed By</th>
                                    <th className="sortable" onClick={toggleSortOrder}>
                                        Date / Time
                                        <span className={`milestone-history__sort-icon ${sortOrder ? "milestone-history__sort-icon--active" : ""}`}>
                                            {sortOrder === "desc" ? "▼" : "▲"}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayedData.map((item) => {
                                    const statusType = getStatusType(item);
                                    const statusLabel = getStatusLabel(item);
                                    return (
                                        <tr key={item.id}>
                                            <td>
                                                <div className="milestone-history__status-cell">
                                                    <span className={`milestone-history__status-icon milestone-history__status-icon--${statusType}`}>
                                                        <StatusIcon type={statusType} />
                                                    </span>
                                                    <span className={`milestone-history__status-badge milestone-history__status-badge--${statusType}`}>
                                                        {statusLabel}
                                                    </span>
                                                </div>
                                            </td>
                                            <td>{item.lastCompletedStage || "—"}</td>
                                            <td>{item.stage || "—"}</td>
                                            <td>
                                                <div className="milestone-history__user-cell">
                                                    <div
                                                        className="milestone-history__user-avatar"
                                                        style={{ backgroundColor: getAvatarColor(item.updatedBy) }}
                                                    >
                                                        {getInitials(item.updatedBy)}
                                                    </div>
                                                    <span className="milestone-history__user-name">{item.updatedBy || "—"}</span>
                                                </div>
                                            </td>
                                            <td>{formatDateTime(item.updatedDateTime)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {sortedData.length > INITIAL_DISPLAY_COUNT && (
                            <div className="milestone-history__toggle-row">
                                <button
                                    className={`milestone-history__toggle-btn ${showAll ? "milestone-history__toggle-btn--expanded" : ""}`}
                                    onClick={() => setShowAll((prev) => !prev)}
                                >
                                    {showAll ? "Show Fewer" : "Show More"}
                                    <ChevronDownIcon />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Navigation Timeline Section */}
            <div className="milestone-history__nav-section">
                <div className="milestone-history__nav-header">
                    <h3 className="milestone-history__nav-title">Milestone Navigation</h3>
                    <p className="milestone-history__nav-subtitle">
                        Track the loan's journey and milestone movements.
                    </p>
                </div>

                <div className="milestone-history__timeline">
                    {sortedData.map((item, index) => {
                        const statusType = getStatusType(item);
                        const statusLabel = getStatusLabel(item);
                        return (
                            <div key={item.id} className="milestone-history__timeline-item">
                                <div className={`milestone-history__timeline-dot milestone-history__timeline-dot--${statusType}`}>
                                    <StatusIcon type={statusType} />
                                </div>
                                <div className="milestone-history__timeline-card">
                                    <div className="milestone-history__timeline-card-header">
                                        <h4 className="milestone-history__timeline-card-title">
                                            {item.lastCompletedStage || item.stage || "—"}
                                        </h4>
                                        <span className={`milestone-history__timeline-card-badge milestone-history__timeline-card-badge--${statusType}`}>
                                            {statusLabel}
                                        </span>
                                    </div>
                                    <p className="milestone-history__timeline-card-date">
                                        {formatDateTime(item.updatedDateTime)}
                                    </p>
                                    <p className="milestone-history__timeline-card-user">
                                        by {item.updatedBy || "Unknown"}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default MilestoneHistoryPage;
